import React from 'react';
import { useAuth } from '../../context/AuthContext';
import { Navigate, useParams, useLocation } from 'react-router-dom';
import { hasAnyRole } from '../../utils/roleUtils';
import { hasModuleAccess } from '../../utils/subscriptionUtils';
import type { ModuleId } from '../../types';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRoles?: string[]; // Array of roles that can access this route
  requiredOrgRole?: string; // Required org-level role (e.g. 'admin')
  fallbackPath?: string; // Where to redirect if access denied (defaults to the locale-aware dashboard)
  requiredModule?: ModuleId;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requiredRoles = [],
  requiredOrgRole,
  fallbackPath,
  requiredModule,
}) => {
  const { user, isAuthenticated, isLoading } = useAuth();
  const { locale } = useParams<{ locale: string }>();
  const location = useLocation();

  // Show loading while checking authentication
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated, saving current path as returnUrl
  if (!isAuthenticated) {
    const currentPath = location.pathname + location.search;
    return <Navigate to={`/${locale || 'en'}/login?returnUrl=${encodeURIComponent(currentPath)}`} replace />;
  }

  const dashboardPath = `/${locale || 'en'}/user-dashboard`;

  // If no specific roles required, allow access to authenticated users
  if (requiredRoles.length === 0 && !requiredModule) {
    return <>{children}</>;
  }

  // Check if user has any of the required roles
  if (requiredRoles.length > 0 && !hasAnyRole(user, requiredRoles)) {
    return <Navigate to={fallbackPath ?? dashboardPath} replace />;
  }

  // Check org-level role (platform admins always pass)
  if (requiredOrgRole) {
    const isPlatformAdmin = hasAnyRole(user, ['admin']);
    const hasOrgRole = user?.org_role === requiredOrgRole;
    if (!isPlatformAdmin && !hasOrgRole) {
      return <Navigate to={dashboardPath} replace />;
    }
  }

  // Subscription-based module gating
  if (requiredModule && !hasModuleAccess(user, requiredModule)) {
    return <Navigate to={dashboardPath} replace />;
  }

  return <>{children}</>;
};